import React, { useEffect, useState } from "react";
import axios from "axios";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import ListAltIcon from "@mui/icons-material/ListAlt";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Alert,
} from "@mui/material";
import toast from "react-hot-toast";

const API = process.env.REACT_APP_API_URL;

// NOTE: `type` prop ab ignore ho raha hai – component library-only hai
const BookingModal = ({ bookingId, type, onClose, onAction }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const [invoiceDialog, setInvoiceDialog] = useState({
    open: false,
    loading: false,
    invoices: [],
    error: "",
  });
  const [markingPaid, setMarkingPaid] = useState({});
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [rejectConfirmation, setRejectConfirmation] = useState({
    open: false,
    loading: false,
  });

  // --- helpers for dates (same logic as student side) ---
  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString("en-GB");
  };

  const getBookingEnd = (inv) =>
    inv.booking_end_date ||
    inv.period_end ||
    inv.expiry_date ||
    inv.end_date ||
    null;

  // ===== Fetch Booking Details (Library-only) =====
  useEffect(() => {
    const fetchDetails = async () => {
      const loadingToast = toast.loading("Loading...");
      try {
        const token = localStorage.getItem("token");
        const url = `${API}library/bookings/${bookingId}/student-details/`;

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.dismiss(loadingToast);
        setData(res.data);
      } catch (err) {
        toast.dismiss(loadingToast);
        setError("❌ Failed to fetch booking details.");
      }
    };

    if (bookingId) fetchDetails();
  }, [bookingId]);

  // ===== Approve / Reject (Library-only) =====
  const handleAction = async (action) => {
    const loadingToast = toast.loading("Loading...");
    try {
      const token = localStorage.getItem("token");
      const url = `${API}library/bookings/${bookingId}/${action}/`;

      await axios.post(
        url,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.dismiss(loadingToast);

      onAction?.();
      onClose();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(
        err?.response?.data?.detail || err?.message || "Request failed"
      );
      setError(`❌ Failed to ${action} booking.`);
    }
  };

  const handleReject = async () => {
    setRejectConfirmation((prev) => ({ ...prev, loading: true }));
    try {
      await handleAction("reject");
      setRejectConfirmation((prev) => ({
        ...prev,
        open: false,
        loading: false,
      }));
    } catch (err) {
      setRejectConfirmation((prev) => ({ ...prev, loading: false }));
    }
  };

  // ===== Admin: Fetch all Library invoices for student =====
  const fetchAllInvoices = async () => {
    setInvoiceDialog((d) => ({ ...d, loading: true, error: "" }));
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const libraryRes = await axios.get(
        `${API}library/admin/library-invoices/?student_id=${data.student.id}`,
        config
      );
      setInvoiceDialog((d) => ({
        ...d,
        invoices: libraryRes.data,
        loading: false,
        error: "",
      }));
    } catch (err) {
      setInvoiceDialog((d) => ({
        ...d,
        loading: false,
        error: "Failed to fetch invoices.",
      }));
    }
  };

  // ===== Admin: Mark Library invoice as paid =====
  const handleMarkPaid = async (invoiceId) => {
    setMarkingPaid((m) => ({ ...m, [invoiceId]: true }));
    try {
      const token = localStorage.getItem("token");
      const url = `${API}library/invoices/${invoiceId}/mark-paid/`;
      await axios.post(
        url,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Invoice marked as paid.");
      // Refresh invoices
      fetchAllInvoices();
      // Refetch parent data if needed
      onAction && onAction();
    } catch (err) {
      toast.error("Failed to mark as paid.");
    } finally {
      setMarkingPaid((m) => ({ ...m, [invoiceId]: false }));
    }
  };

  // ===== Admin: Generate next month Library invoice =====
  const handleGenerateInvoice = async () => {
    setGeneratingInvoice(true);
    const loadingToast = toast.loading("Generating invoice...");
    try {
      const url = `${API}library/generate-next-month-invoice/`;
      const token = localStorage.getItem("token");
      await axios.post(
        url,
        {
          student_id: data.student.id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.dismiss(loadingToast);
      toast.success("Invoice generated successfully.");
      fetchAllInvoices();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to generate invoice."
      );
    } finally {
      setGeneratingInvoice(false);
    }
  };

  if (!data) return null;

  const KeyValueRow = ({ label, value, isStatus = false }) => {
    const isPhone = label.toLowerCase() === "phone";

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: { sm: "space-between" },
          alignItems: { xs: "flex-start", sm: "center" },
          py: 1.5,
          px: 2,
          borderBottom: "1px solid #f3f4f6",
          "&:last-child": { borderBottom: "none" },
          gap: { xs: 0.5, sm: 0 },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "#374151",
            fontSize: "0.875rem",
            minWidth: { sm: "120px" },
            mb: { xs: 0.5, sm: 0 },
          }}
        >
          {label}:
        </Typography>
        <Box
          sx={{
            flex: 1,
            ml: { sm: 2 },
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {isStatus ? (
            <Chip
              label={value.toUpperCase()}
              size="small"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                bgcolor:
                  value === "approved"
                    ? "#dcfce7"
                    : value === "pending"
                    ? "#fef3c7"
                    : "#fef2f2",
                color:
                  value === "approved"
                    ? "#166534"
                    : value === "pending"
                    ? "#d97706"
                    : "#dc2626",
                textTransform: "uppercase",
              }}
            />
          ) : isPhone ? (
            <Typography
              component="a"
              href={`tel:${value}`}
              variant="body2"
              sx={{
                color: "#2563eb",
                fontSize: "0.875rem",
                fontWeight: 500,
                wordBreak: "break-word",
                overflowWrap: "break-word",
                textDecoration: "underline",
                cursor: "pointer",
                "&:hover": {
                  color: "#1d4ed8",
                },
              }}
            >
              {value}
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: "#1f2937",
                fontSize: "0.875rem",
                fontWeight: 500,
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {value}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  // ✅ NEW: safe fallback for ID card URL (optional)
  const idCardUrl =
    data?.student?.id_card ||
    data?.student?.id_card_photo ||
    data?.student?.id_card_image ||
    data?.student?.id_card_url ||
    null;

  // ✅ Documents list (added ID Card, optional)
  const documents = [
    { label: "Aadhaar Front", src: data?.student?.aadhaar_front },
    { label: "Aadhaar Back", src: data?.student?.aadhaar_back },
    { label: "Profile Photo", src: data?.student?.profile_photo },
    { label: "ID Card (Optional)", src: idCardUrl, optional: true },
  ];

  return (
    <>
      {/* ===== Main Booking Modal ===== */}
      <Dialog
        open
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: 4 },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#eff6ff",
            borderBottom: "1px solid #dbeafe",
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 600,
            fontSize: "1.125rem",
            color: "#1e40af",
          }}
        >
          <PersonIcon sx={{ color: "#2563eb" }} />
          Student Details
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, md: 3 }, pb: { xs: 10, md: 12 } }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid
            container
            spacing={{ xs: 2, md: 3 }}
            sx={{ paddingTop: { xs: "5px", md: "10px" } }}
          >
            {/* Personal Information */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: 1,
                  "&:hover": { boxShadow: 2 },
                  transition: "box-shadow 0.3s ease",
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box
                    sx={{
                      p: { xs: 1.5, md: 2 },
                      bgcolor: "#f8fafc",
                      borderBottom: "1px solid #e5e7eb",
                      borderRadius: "8px 8px 0 0",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#1f2937",
                        fontSize: { xs: "0.875rem", md: "1rem" },
                      }}
                    >
                      Personal Information
                    </Typography>
                  </Box>
                  <Box>
                    <KeyValueRow label="Name" value={data.student.full_name} />
                    <KeyValueRow label="Phone" value={data.student.phone} />
                    <KeyValueRow label="Email" value={data.student.email} />
                    <KeyValueRow label="Address" value={data.student.address} />
                    <KeyValueRow
                      label="Education"
                      value={data.student.education}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Booking Information */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: 1,
                  "&:hover": { boxShadow: 2 },
                  transition: "box-shadow 0.3s ease",
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box
                    sx={{
                      p: { xs: 1.5, md: 2 },
                      bgcolor: "#f8fafc",
                      borderBottom: "1px solid #e5e7eb",
                      borderRadius: "8px 8px 0 0",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#1f2937",
                        fontSize: { xs: "0.875rem", md: "1rem" },
                      }}
                    >
                      Booking Information
                    </Typography>
                  </Box>
                  <Box>
                    <KeyValueRow
                      label="Status"
                      value={data.status}
                      isStatus={true}
                    />
                    {/* Library-only seat */}
                    <KeyValueRow label="Seat" value={data.seat} />
                    <KeyValueRow label="Start Date" value={data.start_date} />
                    <KeyValueRow label="End Date" value={data.end_date} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: { xs: 2, md: 3 } }} />

          {/* Documents */}
          <Typography
            variant="h6"
            sx={{
              mb: { xs: 1.5, md: 2 },
              fontWeight: 600,
              color: "#1f2937",
              fontSize: { xs: "0.875rem", md: "1rem" },
            }}
          >
            Documents
          </Typography>

          <Grid
            container
            spacing={{ xs: 1.5, md: 2 }}
            sx={{ mb: { xs: 2, md: 3 } }}
          >
            {documents.map((img, index) => (
              <Grid item xs={12} sm={3} key={index}>
                <Card
                  sx={{
                    borderRadius: 2,
                    boxShadow: 1,
                    "&:hover": { boxShadow: 2 },
                    transition: "box-shadow 0.3s ease",
                    height: "100%",
                  }}
                >
                  <CardContent
                    sx={{ p: { xs: 1.5, md: 2 }, textAlign: "center" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        color: "#374151",
                        fontSize: { xs: "0.75rem", md: "0.875rem" },
                      }}
                    >
                      {img.label}
                    </Typography>

                    {img.src ? (
                      <Box
                        component="img"
                        src={img.src}
                        alt={img.label}
                        sx={{
                          width: { xs: 80, sm: 112 },
                          height: { xs: 80, sm: 112 },
                          objectFit: "cover",
                          borderRadius: 1,
                          cursor: "pointer",
                          "&:hover": { opacity: 0.8 },
                          transition: "opacity 0.3s ease",
                        }}
                        onClick={() => setPreviewImage(img.src)}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: { xs: 80, sm: 112 },
                          height: { xs: 80, sm: 112 },
                          mx: "auto",
                          borderRadius: 1,
                          border: "2px dashed #d1d5db",
                          bgcolor: "#f9fafb",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          px: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "#6b7280", fontWeight: 600 }}
                        >
                          Not Uploaded
                        </Typography>
                      </Box>
                    )}

                    {/* subtle hint only for optional doc */}
                    {img.optional && (
                      <Typography
                        variant="caption"
                        sx={{ display: "block", mt: 0.75, color: "#6b7280" }}
                      >
                        Optional
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>

        {/* Footer buttons */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "flex-end",
            p: 2,
            gap: { xs: 1, sm: 2 },
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ListAltIcon />}
            onClick={() => {
              setInvoiceDialog((d) => ({ ...d, open: true }));
              fetchAllInvoices();
            }}
            sx={{ mb: { xs: 1, sm: 0 } }}
            fullWidth={true}
          >
            View All Invoices
          </Button>
          <Button
            disabled={data.status === "approved"}
            onClick={() => handleAction("approve")}
            variant="contained"
            startIcon={<CheckIcon />}
            sx={{
              bgcolor: data.status === "approved" ? "#9ca3af" : "#059669",
              "&:hover": {
                bgcolor: data.status === "approved" ? "#9ca3af" : "#047857",
              },
              "&:disabled": { bgcolor: "#9ca3af" },
              fontSize: { xs: "0.75rem", md: "0.875rem" },
              py: { xs: 1, md: 1.5 },
            }}
            fullWidth={true}
          >
            Approve
          </Button>
          <Button
            onClick={() =>
              setRejectConfirmation((prev) => ({ ...prev, open: true }))
            }
            variant="contained"
            startIcon={<CancelIcon />}
            sx={{
              bgcolor: "#dc2626",
              "&:hover": { bgcolor: "#b91c1c" },
              fontSize: { xs: "0.75rem", md: "0.875rem" },
              py: { xs: 1, md: 1.5 },
            }}
            fullWidth={true}
          >
            Reject
          </Button>
          <Button
            onClick={onClose}
            variant="outlined"
            startIcon={<CloseIcon />}
            sx={{
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": { bgcolor: "#f9fafb" },
              fontSize: { xs: "0.75rem", md: "0.875rem" },
              py: { xs: 1, md: 1.5 },
            }}
            fullWidth={true}
          >
            Close
          </Button>
        </Box>
      </Dialog>

      {/* Image Preview */}
      {previewImage && (
        <Dialog
          open
          onClose={() => setPreviewImage(null)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              bgcolor: "transparent",
              boxShadow: "none",
            },
          }}
        >
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              bgcolor: "rgba(0,0,0,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1400,
              cursor: "pointer",
            }}
            onClick={() => setPreviewImage(null)}
          >
            <Box
              component="img"
              src={previewImage}
              alt="Preview"
              sx={{
                maxWidth: "90%",
                maxHeight: "90%",
                borderRadius: 2,
                objectFit: "contain",
              }}
            />
          </Box>
        </Dialog>
      )}

      {/* ===== Admin: All Library Invoices Dialog ===== */}
      <Dialog
        open={invoiceDialog.open}
        onClose={() => setInvoiceDialog((d) => ({ ...d, open: false }))}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>All Invoices for {data.student.full_name}</DialogTitle>
        <DialogContent>
          {invoiceDialog.loading ? (
            <Typography>Loading...</Typography>
          ) : invoiceDialog.error ? (
            <Alert severity="error">{invoiceDialog.error}</Alert>
          ) : (
            <>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Library Invoices
              </Typography>

              {/* Desktop Table View */}
              <Box
                sx={{
                  mb: 1,
                  overflowX: "auto",
                  display: { xs: "none", md: "block" },
                }}
              >
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-4 py-4 text-left">Invoice ID</th>
                      <th className="px-4 py-4 text-left">Booking Start</th>
                      <th className="px-4 py-4 text-left">Booking End</th>
                      <th className="px-4 py-4 text-left">Amount</th>
                      <th className="px-4 py-4 text-left">Deposit</th>
                      <th className="px-4 py-4 text-left">Total</th>
                      <th className="px-4 py-4 text-left">Status</th>
                      <th className="px-4 py-4 text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {invoiceDialog.invoices.length === 0 ? (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-4 py-8 text-center text-gray-500"
                        >
                          No library invoices found.
                        </td>
                      </tr>
                    ) : (
                      [...invoiceDialog.invoices]
                        .sort((a, b) => {
                          if (a.generated_on && b.generated_on) {
                            return (
                              new Date(b.generated_on) -
                              new Date(a.generated_on)
                            );
                          }
                          return (b.id || 0) - (a.id || 0);
                        })
                        .map((inv) => {
                          const endRaw = getBookingEnd(inv);
                          return (
                            <tr key={inv.id}>
                              <td className="px-4 py-4 font-mono text-gray-600">
                                {inv.invoice_id}
                              </td>
                              <td className="px-4 py-4 text-gray-600">
                                {formatDate(inv.month)}
                              </td>
                              <td className="px-4 py-4 text-gray-600">
                                {endRaw ? formatDate(endRaw) : "-"}
                              </td>
                              <td className="px-4 py-4 text-gray-600">
                                ₹{inv.amount}
                              </td>
                              <td className="px-4 py-4 text-gray-600">
                                ₹{inv.deposit}
                              </td>
                              <td className="px-4 py-4 font-bold text-gray-800">
                                ₹{inv.total}
                              </td>
                              <td className="px-4 py-4">
                                {inv.is_paid ? (
                                  <span className="inline-block px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-semibold">
                                    Paid
                                  </span>
                                ) : (
                                  <span className="inline-block px-2 py-1 rounded bg-yellow-100 text-yellow-800 text-xs font-semibold">
                                    Unpaid
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-4">
                                {!inv.is_paid && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleMarkPaid(inv.id)}
                                    disabled={markingPaid[inv.id]}
                                  >
                                    {markingPaid[inv.id]
                                      ? "Marking..."
                                      : "Mark as Paid"}
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })
                    )}
                  </tbody>
                </table>
              </Box>

              {/* Mobile Card View */}
              <Box sx={{ display: { xs: "block", md: "none" }, mb: 1 }}>
                {invoiceDialog.invoices.length === 0 ? (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 4,
                      color: "text.secondary",
                    }}
                  >
                    No library invoices found.
                  </Box>
                ) : (
                  [...invoiceDialog.invoices]
                    .sort((a, b) => {
                      if (a.generated_on && b.generated_on) {
                        return (
                          new Date(b.generated_on) - new Date(a.generated_on)
                        );
                      }
                      return (b.id || 0) - (a.id || 0);
                    })
                    .map((inv) => {
                      const endRaw = getBookingEnd(inv);
                      return (
                        <Card
                          key={inv.id}
                          sx={{
                            mb: 2,
                            borderRadius: 2,
                            boxShadow: 1,
                            "&:hover": { boxShadow: 2 },
                            transition: "box-shadow 0.3s ease",
                          }}
                        >
                          <CardContent sx={{ p: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: "monospace",
                                  color: "text.secondary",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {inv.invoice_id}
                              </Typography>
                              {inv.is_paid ? (
                                <Chip
                                  label="Paid"
                                  size="small"
                                  sx={{
                                    bgcolor: "#dcfce7",
                                    color: "#166534",
                                    fontSize: "0.625rem",
                                    fontWeight: 600,
                                  }}
                                />
                              ) : (
                                <Chip
                                  label="Unpaid"
                                  size="small"
                                  sx={{
                                    bgcolor: "#fef3c7",
                                    color: "#d97706",
                                    fontSize: "0.625rem",
                                    fontWeight: 600,
                                  }}
                                />
                              )}
                            </Box>

                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                                mb: 1,
                              }}
                            >
                              {formatDate(inv.month)}
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 1,
                              }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                Booking End:
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {endRaw ? formatDate(endRaw) : "-"}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 1,
                              }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                Amount:
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                ₹{inv.amount}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 1,
                              }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                Deposit:
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                ₹{inv.deposit}
                              </Typography>
                            </Box>

                            <Divider sx={{ my: 1 }} />

                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="body1"
                                fontWeight={600}
                                color="text.primary"
                              >
                                Total: ₹{inv.total}
                              </Typography>

                              {!inv.is_paid && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleMarkPaid(inv.id)}
                                  disabled={markingPaid[inv.id]}
                                  sx={{ ml: 1 }}
                                >
                                  {markingPaid[inv.id]
                                    ? "Marking..."
                                    : "Mark as Paid"}
                                </Button>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
          {data.status === "approved" && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerateInvoice}
              disabled={generatingInvoice}
              sx={{ mb: { xs: 1, sm: 0 }, mr: 2 }}
            >
              {generatingInvoice ? "Generating..." : "Generate Invoice"}
            </Button>
          )}
          <Button
            onClick={() => setInvoiceDialog((d) => ({ ...d, open: false }))}
          >
            Close
          </Button>
        </Box>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <Dialog
        open={rejectConfirmation.open}
        onClose={() =>
          setRejectConfirmation((prev) => ({ ...prev, open: false }))
        }
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: 4 },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#fef2f2",
            borderBottom: "1px solid #fecaca",
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 600,
            fontSize: "1.125rem",
            color: "#dc2626",
          }}
        >
          <CancelIcon sx={{ color: "#dc2626" }} />
          Confirm Rejection
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="body1" sx={{ mb: 2, color: "text.primary" }}>
            Are you sure you want to reject this booking? This action cannot be
            undone.
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Student: <strong>{data?.student?.full_name}</strong>
          </Typography>
        </DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: 2,
            gap: 2,
          }}
        >
          <Button
            onClick={() =>
              setRejectConfirmation((prev) => ({ ...prev, open: false }))
            }
            variant="outlined"
            disabled={rejectConfirmation.loading}
            sx={{
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": { bgcolor: "#f9fafb" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            variant="contained"
            disabled={rejectConfirmation.loading}
            startIcon={<CancelIcon />}
            sx={{
              bgcolor: "#dc2626",
              "&:hover": { bgcolor: "#b91c1c" },
              "&:disabled": { bgcolor: "#9ca3af" },
            }}
          >
            {rejectConfirmation.loading ? "Rejecting..." : "Reject Booking"}
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default BookingModal;
